import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useBlocker, useSearchParams } from 'react-router-dom';
import { holdAppUpdate } from '@app-update';
import { apiFetch } from '../../../../../utils/api';
import { sortByName } from '../../../../../utils/sortingUtils';
import { getSelectedValues, toggleValue, getUnusedValuesForHeader, rulesetHasEmptyCells, rulesetToTsv, extractCellValuesFromRuleSet, extractCellValuesByHeaderFromRuleSet, formatSearchTerm, filterFeaturesForFamily, resolveFamilyIdsForFeatureLookup } from '../../../utils';

const normalizeRuleCellValue = (value) => {
    const normalized = String(value ?? '').trim();
    return normalized === "*" ? "?" : normalized;
};

/**
 * Custom hook for managing rule manager state and data
 * @param {Object} props - Hook props
 * @param {Function} props.onDataChanged - Callback when data changes
 * @param {Function} props.onFeaturesLoaded - Callback when family/feature catalog is loaded
 * @param {Object} props.currentContext - Current context (family)
 * @param {Function} props.setCurrentContext - Set current context
 * @param {number} props.rulesetSyncCount - Incremented when feature ops mutate rule cells server-side
 * @param {number} props.featuresRefreshCount - Incremented when the feature catalog should reload
 * @returns {Object} - Rule manager state and handlers
 */
export function useRuleManager({ onDataChanged, onFeaturesLoaded, currentContext, setCurrentContext, featuresMetadata: featuresMetadataProp, rulesetSyncCount = 0, featuresRefreshCount = 0 }) {
    const [activeProjectId, setActiveProjectId] = useState(localStorage.getItem('activeProjectId') || '');
    const [families, setFamilies] = useState([]);
    const [ruleSets, setRuleSets] = useState([]);
    const [activeRuleSetId, setActiveRuleSetId] = useState(null);
    const [rulesetSearchQuery, setRulesetSearchQuery] = useState('');
    const [rulesetSearchMode, setRulesetSearchMode] = useState('all');
    const [rulesetFeatureFilters, setRulesetFeatureFilters] = useState([]);
    const [rulesetValueFilters, setRulesetValueFilters] = useState([]);
    const [searchHighlight, setSearchHighlight] = useState(null);
    const [rulesetSortBy, setRulesetSortBy] = useState('updatedAt');
    const [rulesetSortOrder, setRulesetSortOrder] = useState('desc');
    const [activeRuleSetData, setActiveRuleSetData] = useState(null);
    const [isRuleSetLoading, setIsRuleSetLoading] = useState(false);
    const [ruleSetLoadFailed, setRuleSetLoadFailed] = useState(false);
    const ruleSetLoadGenerationRef = useRef(0);
    const acceptedRuleSetLoadGenerationRef = useRef(0);
    const [featuresMetadata, setFeaturesMetadata] = useState(featuresMetadataProp || []);

    const [searchParams] = useSearchParams();
    const rulesetIdParam = searchParams.get('rulesetId');
    const familyIdParam = searchParams.get('familyId');
    const currentContextRef = useRef(currentContext);
    currentContextRef.current = currentContext;

    // Modal states
    const [modalType, setModalType] = useState(null);
    const [modalTargetFamily, setModalTargetFamily] = useState(null);
    const [modalTargetRuleSet, setModalTargetRuleSet] = useState(null);
    const [modalTargetFolder, setModalTargetFolder] = useState(null);
    const [modalInputVal, setModalInputVal] = useState("");
    const [deleteRuleSetsCheck, setDeleteRuleSetsCheck] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Consistency Checker States
    const [consistencyResult, setConsistencyResult] = useState(null);
    const [isCheckingConsistency, setIsCheckingConsistency] = useState(false);

    // Draft Grid States
    const [originalRuleSetData, setOriginalRuleSetData] = useState(null);
    const [draftRuleSetData, setDraftRuleSetData] = useState(null);
    const [ruleSetNotes, setRuleSetNotes] = useState([]);

    // Excel Navigation States
    const [focusedCell, setFocusedCell] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editingCellValue, setEditingCellValue] = useState(null);
    const [dropdownCoords, setDropdownCoords] = useState(null);
    const [highlightedOptionIndex, setHighlightedOptionIndex] = useState(-1);

    // Clear highlighted option when not editing
    useEffect(() => {
        if (!isEditing) {
            setHighlightedOptionIndex(-1);
        }
    }, [isEditing]);

    // Update dropdown position when editing
    useEffect(() => {
        if (isEditing && focusedCell) {
            const cellEl = document.querySelector(`[data-cell-row="${focusedCell.rowIndex}"][data-cell-col="${focusedCell.colIndex}"]`);
            if (cellEl) {
                const updateCoords = () => {
                    const rect = cellEl.getBoundingClientRect();
                    const GAP = 4;
                    const MENU_MAX_HEIGHT = 180;
                    const MENU_MAX_WIDTH = 240;
                    const spaceBelow = window.innerHeight - rect.bottom;
                    const spaceAbove = rect.top;
                    // Prefer the side with more room so near-bottom cells fold up
                    // instead of hanging past the viewport and triggering scrollbars.
                    const placeAbove = spaceAbove > spaceBelow;

                    const maxHeight = placeAbove
                        ? Math.min(MENU_MAX_HEIGHT, Math.max(0, spaceAbove - GAP))
                        : Math.min(MENU_MAX_HEIGHT, Math.max(0, spaceBelow - GAP));

                    // Dropdown can grow up to MENU_MAX_WIDTH via max-content; clamp with that.
                    let left = rect.left;
                    if (left + MENU_MAX_WIDTH > window.innerWidth - GAP) {
                        left = Math.max(GAP, window.innerWidth - MENU_MAX_WIDTH - GAP);
                    }

                    setDropdownCoords({
                        top: placeAbove ? null : rect.bottom + GAP,
                        bottom: placeAbove ? window.innerHeight - rect.top + GAP : null,
                        left,
                        width: rect.width,
                        maxHeight
                    });
                };
                updateCoords();
                window.addEventListener('resize', updateCoords);
                window.addEventListener('scroll', updateCoords, true);
                return () => {
                    window.removeEventListener('resize', updateCoords);
                    window.removeEventListener('scroll', updateCoords, true);
                };
            }
        } else {
            setDropdownCoords(null);
        }
    }, [isEditing, focusedCell]);

    // Handle global project change
    useEffect(() => {
        const handleGlobalProjectChange = () => {
            setActiveProjectId(localStorage.getItem('activeProjectId') || '');
        };
        window.addEventListener('activeProjectChanged', handleGlobalProjectChange);
        return () => window.removeEventListener('activeProjectChanged', handleGlobalProjectChange);
    }, []);

    // Sync ruleset ID from URL param
    useEffect(() => {
        if (rulesetIdParam && ruleSets.length > 0) {
            const rs = ruleSets.find(r => r.id === rulesetIdParam || r.parentId === rulesetIdParam);
            if (rs) {
                setActiveRuleSetId(rs.id);
            }
        }
    }, [rulesetIdParam, ruleSets]);

    // Auto-select first ruleset when family changes (do not override a deep link)
    useEffect(() => {
        if (rulesetIdParam) return;
        if (currentContext && currentContext.id) {
            const activeFamily = families.find(f => f.id.toString() === currentContext.id.toString());
            if (activeFamily && activeFamily.ruleSets && activeFamily.ruleSets.length > 0) {
                const hasRs = activeFamily.ruleSets.some(rs => rs.id === activeRuleSetId);
                if (!hasRs) {
                    setActiveRuleSetId(activeFamily.ruleSets[0].id);
                }
            } else {
                setActiveRuleSetId(null);
            }
        } else {
            setActiveRuleSetId(null);
        }
    }, [currentContext, families, rulesetIdParam]);

    // Load menu data with hierarchical endpoint
    const loadMenu = useCallback(async () => {
        const currentProjectId = localStorage.getItem('activeProjectId');
        if (currentProjectId) {
            try {
                const projectRes = await apiFetch(`/api/projects/${currentProjectId}`);
                if (projectRes.ok) {
                    const projectData = await projectRes.json();
                    const linkedFamilyIds = projectData.linkedFamilies || [];

                    // Use hierarchical endpoint with includeFeatures
                    const familiesRes = await apiFetch(`/api/products?projectId=${currentProjectId}&includeFeatures=true&t=${Date.now()}`);

                    if (familiesRes.ok) {
                        const allFamilies = await familiesRes.json();
                        
                        // Extract and deduplicate rulesets from families
                        const allRuleSets = [...new Map(
                            allFamilies.flatMap(f => f.ruleSets || []).map(rs => [rs.id, rs])
                        ).values()];
                        
                        // Extract and deduplicate features from families with source tracking
                        const featuresMap = new Map();
                        allFamilies.forEach(f => {
                            (f.features || []).forEach(feat => {
                                if (!featuresMap.has(feat.id)) {
                                    featuresMap.set(feat.id, { ...feat, sourceFamilyIds: [] });
                                }
                                const existing = featuresMap.get(feat.id);
                                if (!existing.sourceFamilyIds.includes(f.id)) {
                                    existing.sourceFamilyIds.push(f.id);
                                }
                            });
                        });
                        const allFeatures = Array.from(featuresMap.values());

                        const filteredFamilies = sortByName(allFamilies, 'name');
                        const filteredRuleSets = allRuleSets;
                        const filteredFeatures = allFeatures;

                        setFamilies(filteredFamilies);
                        setRuleSets(filteredRuleSets);
                        setFeaturesMetadata(filteredFeatures);
                        onFeaturesLoaded?.(filteredFeatures);

                        // Auto-select first family if currentContext is global, empty, or not linked
                        const applyFamilyContext = (next) => {
                            const current = currentContextRef.current;
                            if (String(current?.id ?? '') === String(next.id ?? '') && (current?.name ?? '') === (next.name ?? '')) {
                                return;
                            }
                            setCurrentContext(next);
                        };

                        if (filteredFamilies.length > 0) {
                            const matchedFamily = filteredFamilies.find(f => f.id.toString() === familyIdParam?.toString());
                            const hasCurrent = filteredFamilies.some(f => f.id.toString() === String(currentContextRef.current?.id ?? ''));

                            if (matchedFamily) {
                                applyFamilyContext({ id: matchedFamily.id, name: matchedFamily.name });
                            } else if (!hasCurrent) {
                                applyFamilyContext({ id: filteredFamilies[0].id, name: filteredFamilies[0].name });
                            }
                        } else {
                            applyFamilyContext({ id: '', name: 'No Family Selected' });
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to load project-filtered menu", err);
            }
        } else {
            setFamilies([]);
            setRuleSets([]);
        }
    }, [familyIdParam, setCurrentContext, onFeaturesLoaded]);

    // Load menu on project change, feature catalog refresh, and when feature ops mutate rule cells
    useEffect(() => {
        loadMenu();
    }, [activeProjectId, loadMenu, rulesetSyncCount, featuresRefreshCount]);

    const familyId = currentContext?.id;
    const reloadActiveRuleSet = useCallback(() => {
        const generation = ++ruleSetLoadGenerationRef.current;
        if (activeRuleSetId && familyId) {
            setIsRuleSetLoading(true);
            setRuleSetLoadFailed(false);
            // Use family-scoped endpoint: need familyId to retrieve ruleset
            apiFetch(`/api/products/${familyId}/rulesets/${activeRuleSetId}?t=${Date.now()}`)
                .then(res => res.ok ? res.json() : Promise.reject('Ruleset not found'))
                .then(data => {
                    if (generation !== ruleSetLoadGenerationRef.current) return;
                    acceptedRuleSetLoadGenerationRef.current = generation;
                    const notes = data.notes || data.Notes || [];
                    setRuleSetNotes(notes);
                    const { notes: _notes, Notes: _Notes, ...rest } = data;
                    setActiveRuleSetData({ ...rest, sourceFamilyIds: [familyId] });
                })
                .catch(err => {
                    if (generation !== ruleSetLoadGenerationRef.current) return;
                    console.error('Failed to load ruleset:', err);
                    setActiveRuleSetData(null);
                    setRuleSetNotes([]);
                    setIsRuleSetLoading(false);
                    setRuleSetLoadFailed(true);
                });
        } else {
            setIsRuleSetLoading(false);
            setRuleSetLoadFailed(false);
            setActiveRuleSetData(null);
            setRuleSetNotes([]);
        }
    }, [activeRuleSetId, familyId]);

    // Load active ruleset data on selection / family change, and when feature ops mutate cells
    useEffect(() => {
        reloadActiveRuleSet();
    }, [reloadActiveRuleSet, rulesetSyncCount]);

    // Sync featuresMetadata with prop
    useEffect(() => {
        if (featuresMetadataProp) {
            setFeaturesMetadata(featuresMetadataProp);
        }
    }, [featuresMetadataProp]);

    const scopedFeaturesMetadata = useMemo(() => {
        const familyIds = resolveFamilyIdsForFeatureLookup(activeRuleSetData, currentContext);
        return filterFeaturesForFamily(featuresMetadata, familyIds);
    }, [featuresMetadata, activeRuleSetData, currentContext]);

    // Setup Local Draft State
    useEffect(() => {
        if (activeRuleSetData) {
            // Deep clone the entire activeRuleSetData, preserving all fields
            const clonedData = JSON.parse(JSON.stringify(activeRuleSetData));
            setOriginalRuleSetData(clonedData);
            setDraftRuleSetData(clonedData);
            setFocusedCell(null);
            setIsEditing(false);
            if (acceptedRuleSetLoadGenerationRef.current === ruleSetLoadGenerationRef.current) {
                setIsRuleSetLoading(false);
            }
        } else {
            setOriginalRuleSetData(null);
            setDraftRuleSetData(null);
            setRuleSetNotes([]);
        }
    }, [activeRuleSetData]);

    const hasChanges = draftRuleSetData && originalRuleSetData && 
        JSON.stringify(draftRuleSetData) !== JSON.stringify(originalRuleSetData);

    useEffect(() => {
        if (!hasChanges) return undefined;
        return holdAppUpdate();
    }, [hasChanges]);

    // React Router Route Navigation Blocker
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            hasChanges && currentLocation.pathname !== nextLocation.pathname
    );

    // Browser Refresh/Leave Interceptor
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasChanges) {
                e.preventDefault();
                e.returnValue = "You have unsaved changes in the rule editor. Do you want to leave?";
                return e.returnValue;
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasChanges]);

    // Handlers for modal actions
    const handleOpenModal = useCallback((e, type, family) => {
        e.stopPropagation();
        setModalType(type);
        setModalTargetFamily(family);
        setModalTargetFolder(null);
        setModalInputVal(type === 'renameFamily' ? family.name : "");
        setDeleteRuleSetsCheck(false);
    }, []);

    const handleOpenCreateFolder = useCallback((family) => {
        setModalType('createFolder');
        setModalTargetFamily(family);
        setModalTargetFolder(null);
        setModalInputVal('');
    }, []);

    const handleOpenRenameFolder = useCallback((folder, family) => {
        setModalType('renameFolder');
        setModalTargetFolder(folder);
        setModalTargetFamily(family || null);
        setModalInputVal(folder?.name || '');
    }, []);

    const handleOpenCreateRulesetInFolder = useCallback((family, folder) => {
        setModalType('createRuleset');
        setModalTargetFamily(family);
        setModalTargetFolder(folder);
        setModalInputVal('');
    }, []);

    const handleCreateRuleSetSubmit = async (e) => {
        e.preventDefault();
        if (!modalInputVal.trim()) return;
        setIsSaving(true);
        try {
            const familyId = modalTargetFamily.id === 'global' ? null : modalTargetFamily.id;
            const res = await apiFetch('/api/rulesets', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Name: modalInputVal.trim(),
                    FamilyId: familyId,
                    FolderId: modalTargetFolder?.id || null
                })
            });
            if (res.ok) {
                const newRs = await res.json();
                setModalType(null);
                setModalInputVal("");
                setModalTargetFolder(null);
                loadMenu();
                onDataChanged();
                setActiveRuleSetId(newRs.id);
            } else {
                alert(`Error creating rule table: ${await res.text()}`);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleRenameFamilySubmit = async (e) => {
        e.preventDefault();
        if (!modalInputVal) return;
        setIsSaving(true);
        try {
            const res = await apiFetch(`/api/products/${modalTargetFamily.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Name: modalInputVal })
            });
            if (res.ok) {
                setModalType(null);
                setModalInputVal("");
                loadMenu();
            } else {
                alert(`Error renaming family: ${await res.text()}`);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteFamilySubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const res = await apiFetch(`/api/products/${modalTargetFamily.id}?deleteRuleSets=${deleteRuleSetsCheck}`, {
                method: 'DELETE'
            });
            if (res.ok) {
                setModalType(null);
                loadMenu();
                onDataChanged();
            } else {
                alert(`Error deleting family: ${await res.text()}`);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCheckConsistency = async (e, family) => {
        e.stopPropagation();
        setModalTargetFamily(family);
        setModalType('consistencyCheck');
        setIsCheckingConsistency(true);
        setConsistencyResult(null);
        try {
            const res = await apiFetch(`/api/products/${family.id}/consistency`);
            if (res.ok) {
                const data = await res.json();
                setConsistencyResult(data);
            } else {
                const text = await res.text();
                setConsistencyResult({ error: `Error checking consistency: ${text || res.statusText}` });
            }
        } catch (err) {
            console.error(err);
            setConsistencyResult({ error: `Network error: ${err.message}` });
        } finally {
            setIsCheckingConsistency(false);
        }
    };

    // Save changes to backend
    const handleSaveChanges = async () => {
        if (!draftRuleSetData) return;
        try {
            // Resolve feature names from featureId for cells that have null featureName
            const resolveFeatureName = (cell) => {
                if (cell.featureName != null && cell.featureName !== '') {
                    return cell;
                }
                if (cell.featureId && featuresMetadata) {
                    const feature = featuresMetadata.find(f => f.id === cell.featureId);
                    if (feature && feature.name) {
                        return { ...cell, featureName: feature.name };
                    }
                }
                // Skip cells with no valid featureName or featureId
                return null;
            };

            const res = await apiFetch(`/api/rulesets/${draftRuleSetData.id}/bulk-save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    headers: draftRuleSetData.headers,
                    rows: draftRuleSetData.rows.map(r => ({
                        rowId: r.rowId,
                        xmin: r.xmin,
                        cells: r.cells.map(resolveFeatureName).filter(c => c !== null).map(c => ({
                            featureName: c.featureName,
                            expectedValue: c.expectedValue ?? '?',
                            cellId: c.cellId ?? null,
                            xmin: c.xmin
                        }))
                    }))
                })
            });
            if (res.ok) {
                // Reload data to finalize and clear drafts
                const updated = await apiFetch(`/api/products/${currentContext.id}/rulesets/${activeRuleSetId}?t=${Date.now()}`)
                    .then(r => r.ok ? r.json() : Promise.reject('Ruleset not found'));
                // Ensure sourceFamilyIds is set for proper filtering
                setActiveRuleSetData({ ...updated, sourceFamilyIds: [currentContext.id] });
                const hasEmptyCells = rulesetHasEmptyCells(updated);
                const headers = Array.isArray(updated.headers) ? updated.headers : [];
                const cellValues = extractCellValuesFromRuleSet(updated);
                const cellValuesByHeader = extractCellValuesByHeaderFromRuleSet(updated);
                setFamilies(prev => prev.map(f => ({
                    ...f,
                    ruleSets: (f.ruleSets || []).map(rs =>
                        String(rs.id) === String(draftRuleSetData.id)
                            ? { ...rs, hasEmptyCells, headers, headersJson: JSON.stringify(headers), cellValues, cellValuesByHeader }
                            : rs
                    )
                })));
                onDataChanged();
            } else {
                const errText = await res.text();
                // Check if this is a concurrency conflict (409)
                if (res.status === 409) {
                    // Parse the error response if it's JSON
                    let errorData;
                    try {
                        errorData = JSON.parse(errText);
                    } catch (e) {
                        errorData = { Message: errText };
                    }
                    // Prompt user to reload and try again
                    const shouldReload = window.confirm(
                        `${errorData.Message || errorData || errText}\n\nReload the ruleset and try again?`
                    );
                    if (shouldReload) {
                        // Reload the ruleset
                        apiFetch(`/api/products/${currentContext.id}/rulesets/${activeRuleSetId}?t=${Date.now()}`)
                            .then(r => r.ok ? r.json() : Promise.reject('Ruleset not found'))
                            .then(data => {
                                setActiveRuleSetData({ ...data, sourceFamilyIds: [currentContext.id] });
                                setOriginalRuleSetData(JSON.parse(JSON.stringify(data)));
                                setDraftRuleSetData(JSON.parse(JSON.stringify(data)));
                            })
                            .catch(err => {
                                console.error('Failed to reload ruleset:', err);
                                alert('Failed to reload ruleset. Please try again.');
                            });
                    }
                } else {
                    alert(`Error saving rule changes: ${errText}`);
                }
            }
        } catch (err) {
            console.error(err);
            alert("Failed to connect to the Rule Engine server.");
        }
    };

    // Excel Cell Focus Navigation Handler
    const handleCellKeyDown = useCallback((e, rowIndex, colIndex) => {
        if (isEditing) return;

        let nextRow = rowIndex;
        let nextCol = colIndex;
        const maxRows = draftRuleSetData?.rows.length + 1 || 1;
        const maxCols = draftRuleSetData?.headers.length || 1;

        if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (rowIndex > 0) nextRow--;
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (rowIndex < maxRows - 1) nextRow++;
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            if (colIndex > 1) nextCol--;
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            if (colIndex < maxCols) nextCol++;
        } else if (e.key === 'Enter') {
            e.preventDefault();
            setIsEditing(true);
        } else if (e.key === ' ' && e.ctrlKey) {
            e.preventDefault();
            setIsEditing(true);
            setHighlightedOptionIndex(0);
        } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            // Typing triggers edit mode with initial character
            e.preventDefault();
            setEditingCellValue(e.key);
            setIsEditing(true);
        }

        if (nextRow !== rowIndex || nextCol !== colIndex) {
            setFocusedCell({ rowIndex: nextRow, colIndex: nextCol });
            setTimeout(() => {
                const cellEl = document.querySelector(`[data-cell-row="${nextRow}"][data-cell-col="${nextCol}"]`);
                if (cellEl) cellEl.focus();
            }, 10);
        }
    }, [isEditing, draftRuleSetData]);

    // Bind Editor input values when enters edit mode
    useEffect(() => {
        if (isEditing && focusedCell && draftRuleSetData) {
            const headerName = draftRuleSetData.headers[focusedCell.colIndex - 1];
            const isVirtualRow = focusedCell.rowIndex === draftRuleSetData.rows.length;
            if (isVirtualRow) {
                if (editingCellValue === null) {
                    setEditingCellValue("");
                }
            } else {
                const row = draftRuleSetData.rows[focusedCell.rowIndex];
                const cell = row?.cells?.find(c => c.featureName === headerName);
                if (editingCellValue === null) {
                    setEditingCellValue(cell ? normalizeRuleCellValue(cell.expectedValue) : "");
                }
            }
        } else {
            setEditingCellValue(null);
        }
    }, [isEditing, focusedCell, draftRuleSetData]);

    // Commit cell edit
    const handleCommitCell = useCallback((rowIndex, headerName, overrideValue = null) => {
        const valToCommit = normalizeRuleCellValue(overrideValue !== null ? overrideValue : editingCellValue);
        if (valToCommit === null) return;
        setDraftRuleSetData(prev => {
            const isVirtualRow = rowIndex === prev.rows.length;
            if (isVirtualRow) {
                if (!valToCommit) {
                    return prev;
                }
                const newRow = {
                    rowId: null,
                    cells: prev.headers.map(h => ({
                        cellId: null,
                        featureName: h,
                        expectedValue: h === headerName ? valToCommit : "?"
                    }))
                };
                return {
                    ...prev,
                    rows: [...prev.rows, newRow]
                };
            } else {
                const updatedRows = prev.rows.map((row, idx) => {
                    if (idx !== rowIndex) return row;
                    const cellIdx = row.cells.findIndex(c => c.featureName === headerName);
                    const newCells = [...row.cells];
                    const newValue = valToCommit || "?";
                    if (cellIdx >= 0) {
                        newCells[cellIdx] = { ...newCells[cellIdx], expectedValue: newValue };
                    } else {
                        newCells.push({ cellId: null, featureName: headerName, expectedValue: newValue });
                    }
                    return { ...row, cells: newCells };
                });
                return { ...prev, rows: updatedRows };
            }
        });
        setIsEditing(false);
        setTimeout(() => {
            const cellEl = document.querySelector(`[data-cell-row="${rowIndex}"][data-cell-col="${focusedCell.colIndex}"]`);
            if (cellEl) cellEl.focus();
        }, 10);
    }, [editingCellValue, focusedCell]);

    // Editor keyboard navigation
    const handleEditorKeyDown = useCallback((e, rowIndex, headerName) => {
        const featureMeta = scopedFeaturesMetadata.find(v => v.name === headerName);
        const valueOptions = Array.isArray(featureMeta?.values)
            ? featureMeta.values.map(v => v.value !== undefined && v.value !== null ? String(v.value) : '')
            : [];
        const options = featureMeta ? ["?", "!", ...valueOptions] : [];

        if (e.key === 'Enter') {
            e.preventDefault();
            handleCommitCell(rowIndex, headerName);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setIsEditing(false);
            setTimeout(() => {
                const cellEl = document.querySelector(`[data-cell-row="${rowIndex}"][data-cell-col="${focusedCell.colIndex}"]`);
                if (cellEl) cellEl.focus();
            }, 10);
        } else if (featureMeta && options.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlightedOptionIndex(prev => (prev + 1) % options.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlightedOptionIndex(prev => (prev - 1 + options.length) % options.length);
            } else if (e.key === ' ' && e.ctrlKey) {
                e.preventDefault();
                setHighlightedOptionIndex(0);
            } else if (e.key === ' ') {
                if (highlightedOptionIndex >= 0 && highlightedOptionIndex < options.length) {
                    e.preventDefault();
                    const newVal = toggleValue(editingCellValue, options[highlightedOptionIndex]);
                    setEditingCellValue(newVal);
                }
            }
        }
    }, [scopedFeaturesMetadata, focusedCell, editingCellValue, highlightedOptionIndex]);

    // Delete a row
    const handleDeleteRowLocally = useCallback((index) => {
        if (!draftRuleSetData) return;
        setDraftRuleSetData(prev => ({
            ...prev,
            rows: prev.rows.filter((_, i) => i !== index)
        }));
    }, [draftRuleSetData]);

    // Include a feature column
    const handleAddFeatureLocally = useCallback((varName) => {
        if (!draftRuleSetData) return;
        if (draftRuleSetData.headers.includes(varName)) return;

        setDraftRuleSetData(prev => ({
            ...prev,
            headers: [...prev.headers, varName],
            rows: prev.rows.map(r => ({
                ...r,
                cells: [...r.cells, { cellId: null, featureName: varName, expectedValue: "?" }]
            }))
        }));
    }, [draftRuleSetData]);

    // Delete a feature column
    const handleDeleteFeatureLocally = async (varName) => {
        if (!draftRuleSetData) return;
        if (!await window.confirm(`Remove feature column '${varName}'? All matching rule cell constraints will be cleared.`)) return;

        setDraftRuleSetData(prev => ({
            ...prev,
            headers: prev.headers.filter(h => h !== varName),
            rows: prev.rows.map(r => ({
                ...r,
                cells: r.cells.filter(c => c.featureName !== varName)
            }))
        }));
        setFocusedCell(null);
        setIsEditing(false);
    };

    // Delete a ruleset (sidebar list item or, if no id given, the active one)
    const handleDeleteRuleSet = async (ruleSetId, ruleSetName) => {
        const id = ruleSetId ?? activeRuleSetId;
        const name = ruleSetName ?? activeRuleSetData?.name;
        if (!id) return;
        if (!await window.confirm(`Are you sure you want to completely delete the rule set '${name}'?`)) return;
        await apiFetch(`/api/rulesets/${id}`, { method: 'DELETE' });
        if (String(id) === String(activeRuleSetId)) {
            setActiveRuleSetId(null);
            setActiveRuleSetData(null);
            setDraftRuleSetData(null);
            setOriginalRuleSetData(null);
            setRuleSetNotes([]);
        }
        loadMenu();
        onDataChanged();
    };

    const handleCopyRuleSetToClipboard = async (ruleSetId) => {
        try {
            let data = null;
            if (String(ruleSetId) === String(activeRuleSetId) && draftRuleSetData) {
                data = draftRuleSetData;
            } else {
                const familyIdForCopy = currentContext?.id;
                if (!familyIdForCopy) {
                    throw new Error('No family selected');
                }
                const res = await apiFetch(`/api/products/${familyIdForCopy}/rulesets/${ruleSetId}`);
                if (!res.ok) throw new Error(await res.text());
                data = await res.json();
            }
            const tsv = rulesetToTsv(data);
            await navigator.clipboard.writeText(tsv);
        } catch (err) {
            console.error('Failed to copy rule table', err);
            alert(`Failed to copy table: ${err.message}`);
        }
    };

    const handleCreateFolderSubmit = async (e) => {
        e.preventDefault();
        const name = (modalInputVal || '').trim();
        if (!name || !modalTargetFamily?.id) return;
        setIsSaving(true);
        try {
            const res = await apiFetch(`/api/products/${modalTargetFamily.id}/folders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Name: name })
            });
            if (res.ok) {
                setModalType(null);
                setModalInputVal('');
                loadMenu();
            } else {
                alert(`Error creating folder: ${await res.text()}`);
            }
        } catch (err) {
            console.error(err);
            alert(`Failed to create folder: ${err.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleRenameFolderSubmit = async (e) => {
        e.preventDefault();
        const name = (modalInputVal || '').trim();
        const familyId = modalTargetFamily?.id || currentContext?.id;
        if (!name || !modalTargetFolder?.id || !familyId) return;
        setIsSaving(true);
        try {
            const res = await apiFetch(`/api/products/${familyId}/folders/${modalTargetFolder.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Name: name })
            });
            if (res.ok) {
                setModalType(null);
                setModalInputVal('');
                setModalTargetFolder(null);
                loadMenu();
            } else {
                alert(`Error renaming folder: ${await res.text()}`);
            }
        } catch (err) {
            console.error(err);
            alert(`Failed to rename folder: ${err.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteFolder = async (folder) => {
        const familyId = currentContext?.id;
        if (!folder?.id || !familyId) return;
        if (!await window.confirm(`Delete folder '${folder.name}'? Tables inside it will be ungrouped, not deleted.`)) return;
        const res = await apiFetch(`/api/products/${familyId}/folders/${folder.id}`, { method: 'DELETE' });
        if (res.ok) {
            loadMenu();
            onDataChanged();
        } else {
            alert(`Error deleting folder: ${await res.text()}`);
        }
    };

    const handleMoveRuleSetToFolder = async (ruleSetId, folderId) => {
        const family = families.find(f => String(f.id) === String(currentContext?.id));
        const current = (family?.ruleSets || []).find(rs => String(rs.id) === String(ruleSetId));
        const currentFolderId = current?.folderId ?? current?.FolderId ?? null;
        const nextFolderId = folderId ?? null;
        if (String(currentFolderId || '') === String(nextFolderId || '')) return;

        const res = await apiFetch(`/api/rulesets/${ruleSetId}/folder`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ FolderId: folderId })
        });
        if (res.ok) {
            loadMenu();
        } else {
            alert(`Error moving rule table: ${await res.text()}`);
        }
    };

    const handleOpenAddComment = (ruleSet) => {
        setModalType('addComment');
        setModalTargetRuleSet(ruleSet);
        setModalInputVal('');
    };

    const handleOpenRenameRuleSet = (ruleSet) => {
        setModalType('renameRuleset');
        setModalTargetRuleSet(ruleSet);
        setModalInputVal(ruleSet?.name || '');
    };

    const handleRenameRuleSetSubmit = async (e) => {
        e.preventDefault();
        const newName = (modalInputVal || '').trim();
        if (!newName || !modalTargetRuleSet?.id) return;
        setIsSaving(true);
        try {
            const res = await apiFetch(`/api/rulesets/${modalTargetRuleSet.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Name: newName })
            });
            if (res.ok) {
                const renamedId = modalTargetRuleSet.id;
                setModalType(null);
                setModalInputVal('');
                setModalTargetRuleSet(null);
                loadMenu();
                if (String(renamedId) === String(activeRuleSetId)) {
                    setDraftRuleSetData(prev => prev ? { ...prev, name: newName } : prev);
                    setOriginalRuleSetData(prev => prev ? { ...prev, name: newName } : prev);
                    setActiveRuleSetData(prev => prev ? { ...prev, name: newName } : prev);
                }
            } else {
                alert(`Error renaming rule table: ${await res.text()}`);
            }
        } catch (err) {
            console.error('Failed to rename rule table', err);
            alert(`Failed to rename rule table: ${err.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddCommentSubmit = async (e) => {
        e.preventDefault();
        if (!modalInputVal.trim() || !modalTargetRuleSet?.id) return;
        setIsSaving(true);
        try {
            const res = await apiFetch(`/api/rulesets/${modalTargetRuleSet.id}/notes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: modalInputVal.trim() })
            });
            if (res.ok) {
                const note = await res.json();
                const commentText = note.text || note.Text || modalInputVal.trim();
                if (String(modalTargetRuleSet.id) === String(activeRuleSetId)) {
                    setRuleSetNotes(prev => [note, ...prev]);
                }
                setFamilies(prev => prev.map(f => ({
                    ...f,
                    ruleSets: (f.ruleSets || []).map(rs => {
                        if (String(rs.id) !== String(modalTargetRuleSet.id)) return rs;
                        const comments = [...(rs.comments || [])];
                        if (commentText && !comments.includes(commentText)) {
                            comments.unshift(commentText);
                        }
                        return { ...rs, comments };
                    })
                })));
                setModalType(null);
                setModalInputVal('');
                setModalTargetRuleSet(null);
            } else {
                alert(`Error adding comment: ${await res.text()}`);
            }
        } catch (err) {
            console.error('Failed to add comment', err);
            alert(`Failed to add comment: ${err.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    const applyTreeSearch = useCallback((kind, featureName, valueName) => {
        const feature = String(featureName || '').trim();
        if (!feature) return;

        setSearchHighlight(null);

        if (kind === 'value') {
            const value = String(valueName ?? '').trim();
            if (!value) return;
            setRulesetSearchQuery(`${formatSearchTerm(feature)}:${formatSearchTerm(value)}`);
            setRulesetSearchMode('values');
            setRulesetFeatureFilters([feature]);
            setRulesetValueFilters([value]);
            return;
        }

        setRulesetSearchQuery(formatSearchTerm(feature));
        setRulesetSearchMode('features');
        setRulesetFeatureFilters([feature]);
        setRulesetValueFilters([]);
    }, []);

    return {
        // State
        activeProjectId,
        families,
        ruleSets,
        activeRuleSetId,
        rulesetSearchQuery,
        rulesetSearchMode,
        rulesetFeatureFilters,
        rulesetValueFilters,
        searchHighlight,
        rulesetSortBy,
        rulesetSortOrder,
        activeRuleSetData,
        isRuleSetLoading,
        ruleSetLoadFailed,
        featuresMetadata,
        originalRuleSetData,
        draftRuleSetData,
        ruleSetNotes,
        focusedCell,
        isEditing,
        editingCellValue,
        dropdownCoords,
        highlightedOptionIndex,
        modalType,
        modalTargetFamily,
        modalTargetRuleSet,
        modalTargetFolder,
        modalInputVal,
        deleteRuleSetsCheck,
        isSaving,
        consistencyResult,
        isCheckingConsistency,
        hasChanges,
        blocker,
        
        // Setters
        setRulesetSearchQuery,
        setRulesetSearchMode,
        setRulesetFeatureFilters,
        setRulesetValueFilters,
        setSearchHighlight,
        setRulesetSortBy,
        setRulesetSortOrder,
        setActiveRuleSetId,
        setActiveRuleSetData,
        setDraftRuleSetData,
        setFocusedCell,
        setIsEditing,
        setEditingCellValue,
        setDropdownCoords,
        setHighlightedOptionIndex,
        setModalType,
        setModalTargetFamily,
        setModalTargetRuleSet,
        setModalInputVal,
        setDeleteRuleSetsCheck,
        
        // Handlers
        loadMenu,
        handleOpenModal,
        handleOpenCreateFolder,
        handleOpenRenameFolder,
        handleOpenCreateRulesetInFolder,
        handleCreateFolderSubmit,
        handleRenameFolderSubmit,
        handleDeleteFolder,
        handleMoveRuleSetToFolder,
        handleCreateRuleSetSubmit,
        handleRenameFamilySubmit,
        handleDeleteFamilySubmit,
        handleCheckConsistency,
        handleSaveChanges,
        handleCellKeyDown,
        handleCommitCell,
        handleEditorKeyDown,
        handleDeleteRowLocally,
        handleAddFeatureLocally,
        handleDeleteFeatureLocally,
        handleDeleteRuleSet,
        handleCopyRuleSetToClipboard,
        handleOpenAddComment,
        handleOpenRenameRuleSet,
        handleAddCommentSubmit,
        handleRenameRuleSetSubmit,
        reloadActiveRuleSet,
        applyTreeSearch,
        
        // Computed
        getUnusedValuesForHeader: (headerName) => getUnusedValuesForHeader(headerName, draftRuleSetData, scopedFeaturesMetadata),
        
        // Current context management
        currentContext,
        setCurrentContext
    };
}
